import React, {useCallback, useEffect, useState} from 'react';
import {SearchInput} from '../../search/SearchInput';
import {useUiStore} from '../../../common/stores/useUiStore';
import {OrganizerDto} from '../../../generated/models/OrganizerDto';
import {
  OrganizerAccountStatus,
  fetchPendingBusinesses,
  updateOrganizerAccountStatus,
} from '../../../common/services/claims';

const buttonBaseStyle: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '1em',
  transition: 'all 0.2s ease',
};

const PendingBusinessRow: React.FC<{
  organizer: OrganizerDto;
  onApprove: () => void;
  onDecline: () => void;
}> = ({organizer, onApprove, onDecline}) => {
  const [hovered, setHovered] = useState<'approve' | 'decline' | null>(null);

  const approveStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: hovered === 'approve' ? '#e6ff33' : '#daff00',
    color: '#1a1a1a',
  };

  const declineStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: hovered === 'decline' ? '#fecaca' : '#fee2e2',
    color: '#991b1b',
  };

  const placeCount = organizer.places?.length ?? 0;

  return (
    <div
      style={{
        backgroundColor: '#222',
        padding: '25px',
        borderRadius: '15px',
        display: 'flex',
        // The buttons wrap under the details on narrow screens.
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '20px',
        width: '100%',
        boxSizing: 'border-box',
      }}>
      <div style={{flex: '1 1 240px', minWidth: 0, overflowWrap: 'anywhere'}}>
        <h3 style={{color: '#fff', margin: 0, fontSize: '1.4rem'}}>
          {organizer.companyName || organizer.name}
        </h3>
        <p style={{color: '#aaa', margin: '4px 0 0', fontSize: '1rem'}}>
          {organizer.name}
          {organizer.businessEmail ? ` — ${organizer.businessEmail}` : ''}
        </p>
        <p style={{color: '#777', margin: '4px 0 0', fontSize: '0.9rem'}}>
          {organizer.vatNo ? `VAT ${organizer.vatNo} · ` : ''}
          {placeCount} {placeCount === 1 ? 'place' : 'places'} claimed
        </p>
      </div>

      <div style={{display: 'flex', gap: '12px', flexShrink: 0}}>
        <button
          style={declineStyle}
          onMouseEnter={() => setHovered('decline')}
          onMouseLeave={() => setHovered(null)}
          onClick={onDecline}>
          Decline
        </button>
        <button
          style={approveStyle}
          onMouseEnter={() => setHovered('approve')}
          onMouseLeave={() => setHovered(null)}
          onClick={onApprove}>
          Approve
        </button>
      </div>
    </div>
  );
};

export const ApproveBusinessScreen: React.FC = () => {
  const setIsLoading = useUiStore(state => state.setIsLoading);
  const [claims, setClaims] = useState<OrganizerDto[] | null>(null);
  const [searchText, setSearchText] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadPendingClaims = useCallback(async () => {
    setIsLoading(true);
    setLoadError(false);
    try {
      setClaims(await fetchPendingBusinesses());
    } catch (error) {
      console.error('Error fetching pending businesses:', error);
      // Not "nothing pending": say the list could not be loaded.
      setClaims(null);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading]);

  useEffect(() => {
    loadPendingClaims();
  }, [loadPendingClaims]);

  const changeStatus = useCallback(
    async (organizerId: string, status: OrganizerAccountStatus) => {
      setIsLoading(true);
      setActionError(null);
      const success = await updateOrganizerAccountStatus(organizerId, status);
      if (success) {
        await loadPendingClaims();
      } else {
        setIsLoading(false);
        setActionError('The change could not be saved. Try again.');
      }
    },
    [loadPendingClaims, setIsLoading],
  );

  const list = claims ?? [];
  const q = searchText.trim().toLowerCase();

  const filtered: OrganizerDto[] = !q
    ? list
    : list.filter(
        org =>
          (org.name ?? '').toLowerCase().includes(q) ||
          (org.companyName ?? '').toLowerCase().includes(q) ||
          (org.businessEmail ?? '').toLowerCase().includes(q) ||
          (org.vatNo ?? '').toLowerCase().includes(q),
      );

  const hasClaims = list.length > 0;

  const emptyMessage =
    list.length === 0
      ? 'There are no businesses pending approval.'
      : 'No businesses match your search.';

  return (
    <div
      style={{
        backgroundColor: '#1a1a1a',
        minHeight: '100vh',
        padding: '40px 20px',
      }}>
      <div style={{maxWidth: '800px', margin: '0 auto'}}>
        <h1 style={{color: '#fff', textAlign: 'center', marginBottom: '30px'}}>
          Approve Business
        </h1>

        {hasClaims && (
          <div
            style={{
              marginBottom: '40px',
              display: 'flex',
              justifyContent: 'center',
            }}>
            <SearchInput
              placeholder="Search businesses..."
              onSearch={setSearchText}
              debounceMs={0}
            />
          </div>
        )}

        {actionError && (
          <p style={{color: '#ff4d4d', textAlign: 'center'}}>{actionError}</p>
        )}

        {loadError && (
          <div style={{textAlign: 'center', marginTop: '40px'}}>
            <p style={{color: '#ff4d4d', fontSize: '1.1rem'}}>
              Could not load the pending businesses.
            </p>
            <button onClick={loadPendingClaims} style={retryButtonStyle}>
              Try again
            </button>
          </div>
        )}

        <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
          {filtered.map(org => (
            <PendingBusinessRow
              key={org.id}
              organizer={org}
              onApprove={() =>
                changeStatus(org.id, OrganizerAccountStatus.Approved)
              }
              onDecline={() =>
                changeStatus(org.id, OrganizerAccountStatus.NotApproved)
              }
            />
          ))}

          {claims !== null && filtered.length === 0 && (
            <p
              style={{
                color: '#666',
                textAlign: 'center',
                fontSize: '1.3rem',
                marginTop: '40px',
              }}>
              {emptyMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const retryButtonStyle: React.CSSProperties = {
  padding: '10px 18px',
  borderRadius: '8px',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  cursor: 'pointer',
  backgroundColor: '#2a2a2a',
  color: '#fff',
  border: '1px solid #333',
};
